import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { X, AlertTriangle, Save } from 'lucide-preact';
export function EditPayloadModal({ isOpen, submissionId, initialPayload, onClose, onSave }) {
    const [payloadText, setPayloadText] = useState('');
    const [jsonError, setJsonError] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (isOpen) {
            try {
                // Pretty print the JSON
                const parsed = JSON.parse(initialPayload);
                setPayloadText(JSON.stringify(parsed, null, 2));
                setJsonError(null);
            }
            catch {
                setPayloadText(initialPayload);
                setJsonError('Warning: Initial payload is not valid JSON');
            }
        }
    }, [isOpen, initialPayload]);
    const validateJson = (text) => {
        try {
            JSON.parse(text);
            setJsonError(null);
            return true;
        }
        catch (e) {
            const error = e;
            setJsonError(`Invalid JSON: ${error.message}`);
            return false;
        }
    };
    const handlePayloadChange = (e) => {
        const target = e.target;
        const newValue = target.value;
        setPayloadText(newValue);
        validateJson(newValue);
    };
    const handleSave = async () => {
        if (!validateJson(payloadText)) {
            return;
        }
        setSaving(true);
        try {
            // Minify the JSON before saving
            const parsed = JSON.parse(payloadText);
            const minified = JSON.stringify(parsed);
            await onSave(submissionId, minified);
            onClose();
        }
        catch (error) {
            const err = error;
            setJsonError(`Failed to save: ${err.message}`);
        }
        finally {
            setSaving(false);
        }
    };
    const handleFormat = () => {
        try {
            const parsed = JSON.parse(payloadText);
            setPayloadText(JSON.stringify(parsed, null, 2));
            setJsonError(null);
        }
        catch (e) {
            const error = e;
            setJsonError(`Cannot format: ${error.message}`);
        }
    };
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4", children: _jsxs("div", { className: "bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-xl font-semibold text-slate-900 dark:text-white", children: "Edit Payload" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-300 mt-1", children: "Modify the JSON payload and retry the submission" })] }), _jsx("button", { onClick: onClose, className: "p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("div", { className: "flex-1 overflow-y-auto p-6", children: [jsonError && (_jsx("div", { className: "mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(AlertTriangle, { className: "w-5 h-5 text-red-600 dark:text-red-400" }), _jsx("div", { className: "text-red-700 dark:text-red-400 text-sm", children: jsonError })] }) })), _jsxs("div", { className: "mb-2 flex items-center justify-between", children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 dark:text-slate-300", children: "JSON Payload" }), _jsx("button", { onClick: handleFormat, className: "text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium", children: "Format JSON" })] }), _jsx("textarea", { value: payloadText, onInput: handlePayloadChange, className: "w-full h-96 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500", spellcheck: false }), _jsx("div", { className: "mt-2 text-xs text-slate-500 dark:text-slate-400", children: "Edit the JSON payload above. The payload will be validated before saving." })] }), _jsxs("div", { className: "flex items-center justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50", children: [_jsx("button", { onClick: onClose, className: "px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors", disabled: saving, children: "Cancel" }), _jsxs("button", { onClick: handleSave, disabled: !!jsonError || saving, className: "inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx(Save, { className: "w-4 h-4" }), saving ? 'Saving...' : 'Save & Retry'] })] })] }) }));
}
